import {
  App,
  Flex,
  Form,
  Image,
  Typography,
  Upload,
  type UploadFile,
  type UploadProps
} from 'antd'
import type { RcFile } from 'antd/es/upload'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Organization } from 'src/api/main'
import { AvatarWithInitials } from 'src/components'
import { useHasWorkspacePermissions } from 'src/hooks'
import { usePresignedUpload } from 'src/hooks/usePresignedUpload'
import {
  isSvgFile,
  MAX_SVG_MB,
  storedPreviewUrl,
  SVG_ACCEPT,
  svgRejection,
  WorkspacePermissionCode
} from 'src/utils'

const { Text } = Typography

type Props = {
  organization?: Organization
}

function fileListFromOrganization(
  organization: Organization | undefined
): UploadFile[] {
  if (!organization?.photo) {
    return []
  }
  return [
    {
      uid: String(organization.photo.id),
      name: organization.photo.title,
      status: 'done',
      url: organization.photo.file,
      thumbUrl: organization.photo.file,
      response: organization.photo
    }
  ]
}

const OrganizationAvatarUpload = ({ organization }: Props) => {
  const { t } = useTranslation()
  // The logo is a workspace attachment: uploading one answers to
  // `attachments.upload`, replacing one also deletes the old.
  const [mayUpload, mayDelete] = useHasWorkspacePermissions([
    WorkspacePermissionCode.ATTACHMENTS_UPLOAD,
    WorkspacePermissionCode.ATTACHMENTS_DELETE
  ])
  const { uploadFile } = usePresignedUpload()
  const [fileList, setFileList] = useState<UploadFile[]>(() =>
    fileListFromOrganization(organization)
  )
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const { message } = App.useApp()
  const customRequest: UploadProps['customRequest'] = async ({
    file,
    onSuccess,
    onError
  }) => {
    try {
      const result = await uploadFile(
        file as RcFile,
        organization === undefined ? undefined : Number(organization.id),
        { isPublic: true }
      )
      onSuccess?.(result, file)
    } catch (error) {
      onError?.(error as Error)
    }
  }

  const onChange: UploadProps['onChange'] = (info) => {
    setFileList(info.fileList)
  }

  const beforeUpload = async (file: RcFile) => {
    if (!isSvgFile(file) && !file.type.startsWith('image/')) {
      message.error(t('only_upload_image'))
      return Upload.LIST_IGNORE
    }
    const svgProblem = await svgRejection(file)
    if (svgProblem) {
      message.error(t(`svgUpload.${svgProblem}`, { size: MAX_SVG_MB }))
      return Upload.LIST_IGNORE
    }
    return true
  }

  return (
    <Flex orientation="vertical" gap="small">
      <Text>{t('logo')}</Text>

      <Form.Item
        name="photo"
        valuePropName="fileList"
        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
      >
        <Upload
          accept={`image/*,${SVG_ACCEPT}`}
          listType="picture-card"
          // The thumbnail is the copy that will be stored, so an SVG shows as sanitized.
          previewFile={storedPreviewUrl}
          customRequest={customRequest}
          maxCount={1}
          disabled={!mayUpload}
          showUploadList={{ showRemoveIcon: mayDelete }}
          onChange={onChange}
          beforeUpload={beforeUpload}
          onRemove={() => {
            setFileList([])
            return true
          }}
          onPreview={(file) => setPreviewUrl(file.url || file.thumbUrl || null)}
        >
          {fileList.length === 0 && (
            <Flex
              orientation="vertical"
              align="center"
              justify="center"
              style={{ height: '100%', width: '100%' }}
            >
              {/* Name only, no id — one colour per workspace name everywhere
                  (sidebar tile, overview hero, onboarding preview). */}
              <AvatarWithInitials
                name={organization?.name ?? ''}
                shape="square"
                size={64}
              />
            </Flex>
          )}
        </Upload>
      </Form.Item>

      {/* Previewed in the page: opened as its own tab, an SVG logo becomes a
          document that runs its own scripts. */}
      {previewUrl && (
        <Image
          src={previewUrl}
          style={{ display: 'none' }}
          preview={{
            open: true,
            onOpenChange: (open) => {
              if (!open) setPreviewUrl(null)
            }
          }}
        />
      )}
    </Flex>
  )
}

export const OrganizationAvatar = ({ organization }: Props) => {
  const uploadKey = `${organization?.id ?? 'none'}-${organization?.photo?.id ?? '0'}`

  return (
    <OrganizationAvatarUpload key={uploadKey} organization={organization} />
  )
}
